import {
	Add,
	DeleteOutline,
	EditOutlined,
	SwapVert,
} from "@mui/icons-material";
import {
	Avatar,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	MenuItem,
	Switch,
	TextField,
	Toolbar,
	Typography,
} from "@mui/material";
import { debounce } from "lodash";
import MUIDataTable from "mui-datatables";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";

import {
	COLLECTION_TYPES,
	deletePopupCollection,
	getPopupCollections,
	updatePopupCollection,
} from "../../apis/PopupCollection";
import { FormDrawer } from "../../components/Form/FormDrawer";
import { FullHeightLoader } from "../../components/Loader/FullHeightLoader";
import { PopupCollectionDrawer } from "../../components/Popup/PopupCollectionDrawer";
import { ReorderCollectionsDrawer } from "../../components/Popup/ReorderCollectionsDrawer";
import { showToast } from "../../utils/toast";

const typeLabel = (value) =>
	COLLECTION_TYPES.find((type) => type.value === value)?.label || value;

export default function PopupCollections() {
	// =========================
	// State
	// =========================

	const [initialLoading, setInitialLoading] = useState(true);
	const [loading, setLoading] = useState(false);
	const [drawerLoading, setDrawerLoading] = useState(false);

	const [collections, setCollections] = useState([]);
	const [count, setCount] = useState(0);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [searchText, setSearchText] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [typeFilter, setTypeFilter] = useState("");

	const [openDrawer, setOpenDrawer] = useState(false);
	const [selectedCollection, setSelectedCollection] = useState(null);

	const [openReorderDrawer, setOpenReorderDrawer] = useState(false);

	const [deletingCollection, setDeletingCollection] = useState(null);
	const [deleting, setDeleting] = useState(false);
	const [updatingFields, setUpdatingFields] = useState({});

	// =========================
	// Debounced Search
	// =========================

	const debouncedFetch = useMemo(
		() =>
			debounce((value) => {
				setSearchText(value);
				setPage(0);
			}, 500),
		[],
	);

	// =========================
	// Effects
	// =========================

	const fetchCollections = useCallback(async () => {
		try {
			setLoading(true);

			const filters = { search: searchText };
			if (typeFilter) {
				filters.collection_type = typeFilter;
			}

			const response = await getPopupCollections(
				rowsPerPage,
				page + 1,
				filters,
			);

			setCollections(response.results || []);
			setCount(response.count || 0);
		} catch (error) {
			console.error(error);
			showToast("Failed to load buckets", "error");
		} finally {
			setLoading(false);
			setInitialLoading(false);
		}
	}, [page, rowsPerPage, searchText, typeFilter]);

	useEffect(() => {
		fetchCollections();
	}, [fetchCollections]);

	useEffect(() => {
		return () => {
			debouncedFetch.cancel();
		};
	}, [debouncedFetch]);

	// =========================
	// Handlers
	// =========================

	const handleAdd = () => {
		setSelectedCollection(null);
		setOpenDrawer(true);
	};

	const handleEdit = (collection) => {
		setSelectedCollection(collection);
		setOpenDrawer(true);
	};

	const handleCloseDrawer = () => {
		setOpenDrawer(false);
		setSelectedCollection(null);
	};

	const handleDelete = async () => {
		setDeleting(true);

		try {
			await deletePopupCollection(deletingCollection.id);

			// Step back a page if the last row on this page was removed
			if (collections.length === 1 && page > 0) {
				setPage(page - 1);
			} else {
				await fetchCollections();
			}

			showToast("Bucket deleted successfully", "success");
			setDeletingCollection(null);
		} catch (error) {
			console.error(error);
			showToast("Failed to delete bucket", "error");
		} finally {
			setDeleting(false);
		}
	};

	const handleToggleField = async (collection, field, value) => {
		const pendingKey = `${collection.id}-${field}`;
		setUpdatingFields((current) => ({ ...current, [pendingKey]: true }));

		try {
			await updatePopupCollection(collection.id, { [field]: value });
			setCollections((current) =>
				current.map((item) =>
					item.id === collection.id
						? { ...item, [field]: value }
						: item,
				),
			);
			showToast(
				`${field === "active" ? "Status" : "Featured"} updated`,
				"success",
			);
		} catch (error) {
			console.error(error);
			showToast(
				`Failed to update ${field === "active" ? "status" : "featured"}`,
				"error",
			);
		} finally {
			setUpdatingFields((current) => {
				const next = { ...current };
				delete next[pendingKey];
				return next;
			});
		}
	};

	// =========================
	// Table Helpers
	// =========================

	const centerColumn = {
		setCellProps: () => ({
			style: {
				textAlign: "center",
			},
		}),
		setCellHeaderProps: () => ({
			style: {
				textAlign: "center",
			},
		}),
	};

	// =========================
	// Table Columns
	// =========================

	const columns = [
		{
			name: "image",
			label: "Image",
			options: {
				...centerColumn,
				customBodyRender: (value) => (
					<Avatar
						variant="square"
						src={value}
						sx={{
							width: 50,
							height: 50,
							borderRadius: 1,
							mx: "auto",
						}}
					/>
				),
			},
		},
		{
			name: "name",
			label: "Name",
			options: {
				...centerColumn,
				customBodyRender: (value) => value || "-",
			},
		},
		{
			name: "popup",
			label: "Popup",
			options: {
				...centerColumn,
				customBodyRender: (value) =>
					typeof value === "object"
						? value?.title || "-"
						: `#${value}`,
			},
		},
		{
			name: "collection_type",
			label: "Type",
			options: {
				...centerColumn,
				customBodyRender: (value) => (
					<Chip
						size="small"
						variant="outlined"
						label={typeLabel(value)}
					/>
				),
			},
		},
		{
			name: "products",
			label: "Products",
			options: {
				...centerColumn,
				customBodyRender: (value) => value?.length ?? 0,
			},
		},
		{
			name: "display_order",
			label: "Order",
			options: centerColumn,
		},
		{
			name: "active",
			label: "Active",
			options: {
				...centerColumn,
				customBodyRender: (value, tableMeta) => {
					const collection = collections[tableMeta.rowIndex];
					const pendingKey = `${collection.id}-active`;

					return (
						<Switch
							checked={Boolean(value)}
							disabled={Boolean(updatingFields[pendingKey])}
							onChange={(event) =>
								handleToggleField(
									collection,
									"active",
									event.target.checked,
								)
							}
							inputProps={{ "aria-label": "Active status" }}
						/>
					);
				},
			},
		},
		{
			name: "featured",
			label: "Featured",
			options: {
				...centerColumn,
				customBodyRender: (value, tableMeta) => {
					const collection = collections[tableMeta.rowIndex];
					const pendingKey = `${collection.id}-featured`;

					return (
						<Switch
							checked={Boolean(value)}
							disabled={Boolean(updatingFields[pendingKey])}
							onChange={(event) =>
								handleToggleField(
									collection,
									"featured",
									event.target.checked,
								)
							}
							inputProps={{ "aria-label": "Featured" }}
						/>
					);
				},
			},
		},
		{
			name: "id",
			label: "Action",
			options: {
				...centerColumn,
				sort: false,
				filter: false,
				customBodyRender: (_, tableMeta) => {
					const collection = collections[tableMeta.rowIndex];

					return (
						<Box display="flex" justifyContent="center" gap={1}>
							<IconButton
								color="primary"
								onClick={() => handleEdit(collection)}
							>
								<EditOutlined />
							</IconButton>

							<IconButton
								color="error"
								onClick={() =>
									setDeletingCollection(collection)
								}
							>
								<DeleteOutline />
							</IconButton>
						</Box>
					);
				},
			},
		},
	];

	// =========================
	// Initial Loader
	// =========================

	if (initialLoading) {
		return <FullHeightLoader />;
	}

	// =========================
	// Render
	// =========================

	return (
		<>
			<ToastContainer />

			<Box p={2}>
				<Toolbar sx={{ gap: 2 }}>
					<Typography variant="h4" noWrap sx={{ flexGrow: 1 }}>
						Popup Buckets
					</Typography>

					<TextField
						select
						size="small"
						label="Type"
						value={typeFilter}
						onChange={(e) => {
							setTypeFilter(e.target.value);
							setPage(0);
						}}
						sx={{ minWidth: 160 }}
					>
						<MenuItem value="">All</MenuItem>
						{COLLECTION_TYPES.map((type) => (
							<MenuItem key={type.value} value={type.value}>
								{type.label}
							</MenuItem>
						))}
					</TextField>

					<Button
						variant="outlined"
						startIcon={<SwapVert />}
						onClick={() => setOpenReorderDrawer(true)}
					>
						Reorder
					</Button>

					<Button
						variant="contained"
						startIcon={<Add />}
						onClick={handleAdd}
					>
						Add Bucket
					</Button>
				</Toolbar>

				<Box sx={{ px: 3, py: 2 }}>
					<MUIDataTable
						title=""
						data={collections}
						columns={columns}
						options={{
							serverSide: true,

							page,
							count,

							rowsPerPage,
							rowsPerPageOptions: [10, 25, 50],

							selectableRows: "none",
							responsive: "standard",
							download: false,
							print: false,
							filter: false,
							viewColumns: false,
							sort: false,

							search: true,
							searchText: searchInput,

							onSearchChange: (text) => {
								setSearchInput(text || "");
								debouncedFetch(text || "");
							},

							onTableChange: (action, tableState) => {
								switch (action) {
									case "changePage":
										setPage(tableState.page);
										break;

									case "changeRowsPerPage":
										setRowsPerPage(tableState.rowsPerPage);
										setPage(0);
										break;

									default:
										break;
								}
							},
						}}
						isLoading={loading}
					/>
				</Box>
			</Box>

			<FormDrawer
				open={openDrawer}
				setEditOpen={handleCloseDrawer}
				loading={drawerLoading}
			>
				<PopupCollectionDrawer
					collection={selectedCollection}
					setEditOpen={handleCloseDrawer}
					reloadTable={fetchCollections}
					setLoading={setDrawerLoading}
				/>
			</FormDrawer>

			<FormDrawer
				open={openReorderDrawer}
				setEditOpen={setOpenReorderDrawer}
				loading={drawerLoading}
			>
				<ReorderCollectionsDrawer
					setEditOpen={setOpenReorderDrawer}
					reloadTable={fetchCollections}
					setLoading={setDrawerLoading}
				/>
			</FormDrawer>

			<Dialog
				open={Boolean(deletingCollection)}
				onClose={() => !deleting && setDeletingCollection(null)}
			>
				<DialogTitle>Delete Bucket</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete
						{deletingCollection?.name
							? ` "${deletingCollection.name}"`
							: " this bucket"}
						? This cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeletingCollection(null)}
						disabled={deleting}
					>
						Cancel
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={handleDelete}
						disabled={deleting}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
